import type {CSSProperties, MouseEventHandler, ReactNode} from 'react'
import {cn} from '@/lib/utils'

type GapSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type AlignItems = 'start' | 'center' | 'end' | 'baseline' | 'stretch'
type JustifyContent = 'start' | 'center' | 'end' | 'between'
type InlineRowSurface = 'hoverable' | 'hoverableLink' | 'toolbar' | 'toolbarTop'
type InlineRowGrow = boolean
type InlineRowMinWidth = 'zero' | 'xs'

interface InlineRowProps {
    children: ReactNode
    gap?: GapSize
    align?: AlignItems
    justify?: JustifyContent
    wrap?: boolean
    /**
     * Visual surface treatment for the row. `hoverable` gives padded, rounded
     * list-row styling used for clickable/hoverable entries in a list.
     * `hoverableLink` adds a subtle text-size shift for link rows.
     */
    surface?: InlineRowSurface
    /** `flex-1` — fills remaining horizontal space inside a flex parent. */
    grow?: InlineRowGrow
    /** `min-w-0` (zero) or `min-w-[80px]` (xs) — required for truncation inside flex layouts. */
    minWidth?: InlineRowMinWidth
    className?: string
    style?: CSSProperties
    onClick?: MouseEventHandler<HTMLDivElement>
    'data-testid'?: string
    [key: `data-${string}`]: string | undefined
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const gapClasses: Record<GapSize, string> = {
    xs: 'gap-1',
    sm: 'gap-1.5',
    md: 'gap-2',
    lg: 'gap-3',
    xl: 'gap-4',
}
const alignClasses: Record<AlignItems, string> = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    baseline: 'items-baseline',
    stretch: 'items-stretch',
}
const justifyClasses: Record<JustifyContent, string> = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
}
const surfaceClasses: Record<InlineRowSurface, string> = {
    hoverable: 'py-2 px-2 rounded hover:bg-muted/50',
    hoverableLink: 'p-2 rounded hover:bg-muted/50 transition-colors text-sm',
    toolbar: 'px-4 py-2 border-b',
    toolbarTop: 'px-4 py-2 border-t',
}
const minWidthClasses: Record<InlineRowMinWidth, string> = {
    zero: 'min-w-0',
    xs: 'min-w-[80px]',
}
/* eslint-enable i18next/no-literal-string */

export function InlineRow({
    children,
    gap = 'md',
    align = 'center',
    justify,
    wrap,
    surface,
    grow,
    minWidth,
    className,
    style,
    onClick,
    'data-testid': testId,
    ...rest
}: InlineRowProps) {
    return (
        <div
            className={cn(
                'flex',
                alignClasses[align],
                justify && justifyClasses[justify],
                gapClasses[gap],
                wrap && 'flex-wrap',
                grow && 'flex-1',
                minWidth && minWidthClasses[minWidth],
                surface && surfaceClasses[surface],
                className,
            )}
            style={style}
            onClick={onClick}
            data-testid={testId}
            {...rest}
        >
            {children}
        </div>
    )
}
